// LOS CÓDIGOS DE UN PRODUCTO, DEL LADO DEL CLIENTE · U-02 (DISEÑO §6.2).
//
// Fase a: cómo se le dice a cada tipo en la pantalla. La base guarda
// `ITF14`, `GS1_128`, `QR_AJENO`…, que son nombres de máquina; quien mira
// la ficha necesita saber QUÉ ES el código («la caja o el empaque»), no
// cómo se llama la simbología. La fase b añade aquí la validación en vivo.
//
// Los doce son los del CHECK `producto_codigo_tipo_valido` de la 244. Si la
// base gana un tipo, sale «Otro tipo de código» en vez del nombre crudo: un
// nombre de máquina en la interfaz es jerga, y un hueco no dice nada.

export const NOMBRE_DEL_TIPO = Object.freeze({
  EAN13: 'EAN-13',
  EAN8: 'EAN-8',
  UPCA: 'UPC-A',
  UPCE: 'UPC-E',
  // D1: GTIN-14 es el tipo ITF14 de siempre; en la interfaz se dice GTIN-14.
  ITF14: 'GTIN-14 (caja o empaque)',
  CODE128: 'Code 128',
  CODE39: 'Code 39',
  INTERNO: 'Código interno',
  DATAMATRIX: 'Data Matrix',
  GS1_128: 'GS1-128',
  QR_TEGO: 'QR de TEGO',
  QR_AJENO: 'QR de otro sistema',
});

/** El nombre humano de un tipo de código; nunca el nombre de máquina. */
export function nombreDelTipo(tipo) {
  if (!tipo) return 'Tipo sin indicar';
  return NOMBRE_DEL_TIPO[String(tipo).toUpperCase()] ?? 'Otro tipo de código';
}

// =====================================================================
// FASE c · EL NIVEL DE EMPAQUE (migración 253, DISEÑO §2.2 y §6.2)
// =====================================================================
// Qué se tiene en la mano al escanear el código. Los cuatro valores son
// los del CHECK `producto_codigo_nivel_valido` de la 253. LOS NIVELES SE
// ENUMERAN EN TRES SITIOS DE ESTE ARCHIVO —la lista, sus nombres y sus
// artículos— y la prueba 36b del servidor lee los tres y los compara con
// la base, con `accesos/empaque.py` y con `CodigoCrear` (auditoría U-02 c,
// H4: una opción sin nombre saldría como un círculo sin texto). La ficha
// no escribe ningún nivel por su cuenta: los toma de aquí. `unidades`
// sigue mandando en la cantidad: el nivel es para leer.
export const NIVELES_EMPAQUE = Object.freeze(['PIEZA', 'PAQUETE', 'CAJA', 'TARIMA']);

export const NOMBRE_DEL_NIVEL = Object.freeze({
  PIEZA: 'Pieza',
  PAQUETE: 'Paquete',
  CAJA: 'Caja',
  TARIMA: 'Tarima',
});

// Para el motivo de «Añadir»: «escribe cuántas piezas trae la caja».
export const ARTICULO_DEL_NIVEL = Object.freeze({
  PIEZA: 'la pieza',
  PAQUETE: 'el paquete',
  CAJA: 'la caja',
  TARIMA: 'la tarima',
});

// Lo que la ficha propone al elegir cada nivel. Solo la caja tiene un
// número que casi todo el mundo reconoce (la de doce); paquete y tarima
// cambian tanto de un producto a otro que TEGO no inventa uno y lo pide.
export const UNIDADES_PROPUESTAS = Object.freeze({ PIEZA: '1', CAJA: '12' });

/** El nombre humano de un nivel, o null si no consta. */
export function nombreDelNivel(nivel) {
  if (!nivel) return null;
  return NOMBRE_DEL_NIVEL[String(nivel).toUpperCase()] ?? null;
}

/** EL NIVEL QUE SE PROPONE AL TECLEAR UN CÓDIGO (auditoría U-02 c, H1).
 *  En un GTIN-14 el primer dígito es el indicador del empaque (GS1):
 *    · 0 es la propia pieza escrita con un cero delante (0 + EAN-13, el
 *      mismo código para TEGO desde la fase b) → Pieza;
 *    · 1 a 8 es un empaque que agrupa piezas → Caja, con 12;
 *    · 9 es un producto de peso o medida variable → Pieza, y la ayuda
 *      `codigos.alta.gtin14` lo explica.
 *  Cualquier otro código → Pieza. Proponer Caja con indicador 0 hacía que
 *  cada escaneo de la pieza metiera 12. */
export function nivelPropuesto(codigo, tipo) {
  if (String(tipo ?? '').toUpperCase() !== 'ITF14') return 'PIEZA';
  const indicador = String(codigo ?? '').replace(/\s+/g, '').charAt(0);
  return /^[1-8]$/.test(indicador) ? 'CAJA' : 'PIEZA';
}

/** «GTIN-14», «EAN-13»… sin la aclaración larga: para la línea corta de la
 *  lista, donde el nivel ya dice si es la caja. */
export function nombreCortoDelTipo(tipo) {
  const t = tipo ? String(tipo).toUpperCase() : '';
  return NOMBRES_GS1[t] ?? nombreDelTipo(tipo);
}

// LAS UNIDADES DE MEDIDA DE UN PRODUCTO (`UNIDADES` de `schemas.py`), con
// su singular, su plural y si se dice «cuántas» o «cuántos» (auditoría
// U-02 c, H5: la lista decía «10 piezas» de un producto en kilos, y el
// movimiento «12 caja»). Una que no esté aquí se escribe tal cual.
const UNIDAD_DE_MEDIDA = Object.freeze({
  PIEZA: ['pieza', 'piezas', 'f'],
  PZA: ['pieza', 'piezas', 'f'],
  CAJA: ['caja', 'cajas', 'f'],
  PAQUETE: ['paquete', 'paquetes', 'm'],
  KG: ['kg', 'kg', 'm'],
  G: ['g', 'g', 'm'],
  L: ['L', 'L', 'm'],
  ML: ['ml', 'ml', 'm'],
  M: ['m', 'm', 'm'],
  CM: ['cm', 'cm', 'm'],
});

function laUnidad(unidadMedida) {
  const um = unidadMedida ? String(unidadMedida).trim() : '';
  if (!um || /^(pz|pza|pzas|pieza|piezas)$/i.test(um)) return UNIDAD_DE_MEDIDA.PIEZA;
  const u = um.toUpperCase();
  return UNIDAD_DE_MEDIDA[u] ?? [um.toLowerCase(), um.toLowerCase(), 'f'];
}

/** «12 piezas», «1 pieza», «2.5 kg», «12 cajas». Sin unidad de medida, o
 *  con la de piezas, dice piezas; con otra, la suya con su plural. */
export function cuantasUnidades(unidades, unidadMedida = null) {
  const n = Number(unidades);
  const cifra = Number.isFinite(n) ? String(n) : String(unidades ?? '');
  const [una, varias] = laUnidad(unidadMedida);
  return `${cifra} ${n === 1 ? una : varias}`;
}

/** La línea de un código en la lista de la ficha: «Caja · 12 piezas ·
 *  GTIN-14», o «Caja · 10 kg · GTIN-14» si el producto se mide en kilos.
 *  Sin nivel (los códigos de antes de la 253), como antes:
 *  «GTIN-14 (caja o empaque) · 12 unidades por código». */
export function lineaDelCodigo(c, unidadMedida = null) {
  const nivel = nombreDelNivel(c?.nivel_empaque);
  const partes = [];
  if (nivel) {
    partes.push(nivel, cuantasUnidades(c.unidades, unidadMedida), nombreCortoDelTipo(c.tipo));
  } else {
    partes.push(nombreDelTipo(c?.tipo));
    if (Number(c?.unidades) !== 1) partes.push(`${Number(c.unidades)} unidades por código`);
  }
  if (c?.descripcion) partes.push(c.descripcion);
  return partes.join(' · ');
}

/** La línea del movimiento al escanear: «Caja: 12 piezas por escaneo.»,
 *  con la unidad del producto. Sin nivel, el código por su descripción o
 *  su tipo con nombre humano (nunca «itf14»). */
export function lineaDePresentacion(presentacion, unidadMedida = null) {
  if (!presentacion?.unidades) return null;
  const cuantas = cuantasUnidades(presentacion.unidades, unidadMedida);
  const nivel = nombreDelNivel(presentacion.nivel_empaque);
  if (nivel) return `${nivel}: ${cuantas} por escaneo.`;
  const que = presentacion.descripcion || nombreDelTipo(presentacion.tipo);
  return `Código de ${que}: ${cuantas} por escaneo.`;
}

// LO QUE CABE EN `unidades` (NUMERIC(14,3)), la misma regla que
// `CodigoCrear` en el servidor (auditoría U-02 c, H3): mayor que cero,
// hasta 11 enteros y hasta 3 decimales.
const UNIDADES_QUE_CABEN = /^\d{1,11}(\.\d{1,3})?$/;

/** Por qué «Añadir» sigue apagado por las unidades, en una frase que dice
 *  qué hacer; '' si están bien. Se pinta tras «Para seguir, ». */
export function motivoDeUnidades(texto, nivel, unidadMedida = null) {
  const t = String(texto ?? '').trim();
  const [, varias, genero] = laUnidad(unidadMedida);
  const cuantas = genero === 'f' ? 'cuántas' : 'cuántos';
  const que = ARTICULO_DEL_NIVEL[nivel] ?? 'este código';
  if (/^\d+\.\d{4,}$/.test(t)) {
    return 'escribe las unidades con 3 decimales como máximo, por ejemplo 0.125';
  }
  if (/^\d{12,}(\.\d*)?$/.test(t)) {
    return `ese número es demasiado grande: revisa ${cuantas} ${varias} trae ${que}`;
  }
  if (!UNIDADES_QUE_CABEN.test(t) || !(Number(t) > 0)) {
    return `escribe ${cuantas} ${varias} trae ${que}: un número mayor que cero`;
  }
  return '';
}

// =====================================================================
// FASE b · LA MISMA REGLA QUE EL SERVIDOR, MIENTRAS SE ESCRIBE
// =====================================================================
// Gemelas de `backend/app/codigos.py`: `digitoVerificador`, `upceAUpca`,
// `gtin14`, `formasGtin14` y `detectarTipo` dan lo mismo que sus pares de
// Python sobre los MISMOS vectores (`backend/app/accesos/codigos_vectores.json`,
// prueba 32). Si un lado cambia un peso y el otro no, el campo diría
// «válido» a un código que el servidor rechaza: un verde que miente.

export const LONGITUDES = Object.freeze({ EAN13: 13, EAN8: 8, UPCA: 12, ITF14: 14, UPCE: 8 });
export const TIPOS_GTIN = Object.freeze(['EAN8', 'EAN13', 'UPCA', 'ITF14', 'UPCE']);
// Los que el servidor guarda en mayúsculas (`codigos.MAYUSCULAS`). Un QR
// ajeno, un Data Matrix y un GS1-128 distinguen mayúsculas y van tal cual.
export const MAYUSCULAS = Object.freeze(['EAN13', 'EAN8', 'UPCA', 'UPCE', 'ITF14', 'CODE128', 'INTERNO', 'CODE39']);
// El alfabeto y la longitud de `producto_codigo_formato`.
export const LARGO_MINIMO = 4;
export const LARGO_MAXIMO = 120;
const PATRON = /^[A-Za-z0-9._:/()-]+$/;
const DIGITOS = /^[0-9]+$/;
// UNA URL ES UN QR AJENO y se guarda TAL CUAL (auditoría U-02 b, H3): en un
// enlace corto las mayúsculas son parte de la dirección. Gemela de
// `codigos._URL` / `es_url` del servidor.
const URL_WEB = /^https?:\/\//i;

/** ¿Lo leído es una dirección web (http:// o https://)? */
export function esUrl(codigo) {
  return URL_WEB.test(String(codigo ?? '').trim());
}

// EL QR DE TEGO DENTRO DE SU URL (auditoría U-02 b segunda, N4): la
// etiqueta lleva `https://<el dominio que sea>/q/<qr_tego>` (producto) o
// `…/qp/<qr_tego>` (pieza). El escáner lo desenvuelve antes de buscar, así
// que añadido como «QR de otro sistema» a otro producto nunca lo abriría.
// Gemela de `codigos.qr_de_tego` del servidor, con la misma regla.
const PREFIJOS_TEGO = [['/qp/', 'pieza'], ['/q/', 'producto']];

/** 'producto' o 'pieza' si es la URL del QR de TEGO de uno u otro; si no, null. */
export function esQrDeTego(codigo) {
  if (!esUrl(codigo)) return null;
  const c = String(codigo).trim();
  for (const [prefijo, que] of PREFIJOS_TEGO) {
    const i = c.lastIndexOf(prefijo);
    if (i >= 0 && c.slice(i + prefijo.length).replace(/^[/ ]+|[/ ]+$/g, '') !== '') return que;
  }
  return null;
}

/** El mismo texto que da el servidor (`codigos.mensaje_qr_de_tego`). */
export function mensajeQrDeTego(que) {
  if (que === 'pieza') {
    return 'Ese es el código QR de TEGO de una pieza: ya la identifica, no hace falta añadirlo. '
      + 'Para abrirla, escanéalo desde «Escanear»; aquí se añaden los códigos del fabricante o de otro sistema.';
  }
  return 'Ese es el código QR de TEGO de un producto: ya lo identifica, no hace falta añadirlo. '
    + 'Para abrir su producto, escanéalo desde «Escanear»; aquí se añaden los códigos del fabricante o de otro sistema.';
}
const NOMBRES_GS1 = Object.freeze({ EAN13: 'EAN-13', EAN8: 'EAN-8', UPCA: 'UPC-A', UPCE: 'UPC-E', ITF14: 'GTIN-14' });

/** Dígito de control GS1: pesos 3 y 1 de derecha a izquierda. */
export function digitoVerificador(digitos) {
  let total = 0;
  const d = String(digitos);
  for (let i = 0; i < d.length; i += 1) {
    total += Number(d[d.length - 1 - i]) * (i % 2 === 0 ? 3 : 1);
  }
  return (10 - (total % 10)) % 10;
}

function gs1Valido(codigo) {
  return DIGITOS.test(codigo) && codigo.length >= 2
    && digitoVerificador(codigo.slice(0, -1)) === Number(codigo.slice(-1));
}

/** Expande un UPC-E de 8 dígitos (sistema 0 o 1) a su UPC-A, o null. */
export function upceAUpca(codigo) {
  const c = String(codigo ?? '');
  if (!DIGITOS.test(c) || c.length !== 8) return null;
  const sistema = c[0];
  const d = c.slice(1, 7);
  const control = c[7];
  if (sistema !== '0' && sistema !== '1') return null;
  const ultimo = d[5];
  let cuerpo;
  if ('012'.includes(ultimo)) cuerpo = d.slice(0, 2) + ultimo + '0000' + d.slice(2, 5);
  else if (ultimo === '3') cuerpo = d.slice(0, 3) + '00000' + d.slice(3, 5);
  else if (ultimo === '4') cuerpo = d.slice(0, 4) + '00000' + d[4];
  else cuerpo = d.slice(0, 5) + '0000' + ultimo;
  return sistema + cuerpo + control;
}

/** La forma GTIN-14 de un código numérico válido, o null. Nunca lanza. */
export function gtin14(codigo, tipo) {
  if (!codigo || !tipo || !TIPOS_GTIN.includes(tipo)) return null;
  if (tipo === 'UPCE') {
    const upca = upceAUpca(codigo);
    if (upca === null || !gs1Valido(upca)) return null;
    return upca.padStart(14, '0');
  }
  if (!DIGITOS.test(codigo) || codigo.length !== LONGITUDES[tipo] || !gs1Valido(codigo)) return null;
  return codigo.padStart(14, '0');
}

/** Las formas GTIN-14 que puede tener una lectura sin tipo. */
export function formasGtin14(lectura) {
  const l = String(lectura ?? '').trim();
  if (!DIGITOS.test(l)) return [];
  const porLargo = { 8: ['EAN8', 'UPCE'], 12: ['UPCA'], 13: ['EAN13'], 14: ['ITF14'] };
  const formas = [];
  for (const tipo of porLargo[l.length] || []) {
    const f = gtin14(l, tipo);
    if (f !== null && !formas.includes(f)) formas.push(f);
  }
  return formas;
}

/** El tipo que el servidor deduce cuando no se le dice (`detectar_tipo`). */
export function detectarTipo(codigo) {
  const c = String(codigo ?? '');
  if (esUrl(c)) return 'QR_AJENO';
  if (!DIGITOS.test(c)) return 'CODE128';
  if (c.length === 8) {
    if (!gs1Valido(c) && gtin14(c, 'UPCE') !== null) return 'UPCE';
    return 'EAN8';
  }
  for (const tipo of ['EAN13', 'UPCA', 'ITF14']) {
    if (c.length === LONGITUDES[tipo]) return tipo;
  }
  return 'INTERNO';
}

/** ¿El código cumple su dígito verificador? (EAN, UPC-A, UPC-E, GTIN-14). */
export function digitoCorrecto(codigo, tipo) {
  if (tipo === 'UPCE') {
    const upca = upceAUpca(codigo);
    return upca !== null && gs1Valido(upca);
  }
  return gs1Valido(codigo);
}

/**
 * Lo que el campo deja escrito (auditoría U-02 b, H2):
 *   · lo que llega del LECTOR o de la CÁMARA no se toca: solo se recortan
 *     los espacios de las orillas. Llega exacto, y es lo que el escáner
 *     recibirá la próxima vez que lea esa etiqueta;
 *   · lo TECLEADO o pegado pierde los espacios (ningún código los admite:
 *     «7501 0553 6351 3» queda «7501055363513») y las letras van en
 *     mayúsculas en los tipos que el servidor guarda así;
 *   · LOS GUIONES, solo al TECLEAR y solo si lo que queda sin guiones ni
 *     espacios es un GTIN válido (EAN-8/13, UPC-A/E, GTIN-14 con su
 *     dígito bien): «7501-0553-6351-3», como lo imprime una etiqueta o un
 *     ISBN, queda «7501055363513», que es lo que el lector mandará
 *     (DISEÑO §6.2; auditoría U-02 b segunda, N3). En cualquier otro caso
 *     se quedan: «1234-5678» tecleado no da un GTIN válido y es un Code 128
 *     con guion; «9638-5074» que llega del lector o de la cámara llega
 *     exacto (H2). Con un tipo dicho que no es GTIN (Code 128, interno),
 *     el guion es parte del código y también se queda.
 * Un QR ajeno (una URL) se deja tal cual en los dos casos.
 */
export function limpiarCodigo(texto, tipo = null, medio = null) {
  if (medio === 'LECTOR' || medio === 'CAMARA') return String(texto ?? '').trim();
  const sinEspacios = String(texto ?? '').replace(/\s+/g, '');
  if (medio === 'TECLADO' && sinEspacios.includes('-') && !esUrl(sinEspacios)) {
    const sinGuiones = sinEspacios.replace(/-/g, '');
    const tipoDicho = tipo ? String(tipo).toUpperCase() : null;
    const esGtin = tipoDicho
      ? TIPOS_GTIN.includes(tipoDicho) && gtin14(sinGuiones, tipoDicho) !== null
      : formasGtin14(sinGuiones).length > 0;
    if (esGtin) return sinGuiones;
  }
  const t = tipo || detectarTipo(sinEspacios);
  return MAYUSCULAS.includes(t) ? sinEspacios.toUpperCase() : sinEspacios;
}


/**
 * El estado de un código para la línea de debajo del campo y para el botón.
 * Devuelve `{ nivel, texto, motivo, tipo, listo }`:
 *   verde    el dígito verificador cuadra («EAN-13 válido»);
 *   amarillo le faltan caracteres (incluye un numérico de 4 a 7 o de 9 a 11
 *            dígitos: a medio escribir, o interno si así se marca);
 *   rojo     el servidor lo rechazaría (dígito, caracteres, largo);
 *   gris     tipo sin dígito que comprobar;
 *   vacio    nada escrito.
 * `motivo` es lo que dice el botón apagado; `listo` dice si se puede mandar.
 * Lo verde y lo gris es exactamente lo que `codigos.validar` acepta.
 */
export function estadoDelCodigo(texto, tipo = null) {
  const c = String(texto ?? '');
  const t = tipo ? String(tipo).toUpperCase() : null;
  if (c === '') {
    return { nivel: 'vacio', texto: '', motivo: 'escribe o escanea el código', tipo: null, listo: false };
  }
  if (c.length > LARGO_MAXIMO) {
    return { nivel: 'rojo', tipo: t,
      texto: `Un código lleva hasta ${LARGO_MAXIMO} caracteres y este trae ${c.length}: revisa que sea uno solo.`,
      motivo: 'acorta el código', listo: false };
  }
  if (!PATRON.test(c)) {
    return { nivel: 'rojo', tipo: t,
      texto: 'Hay caracteres que no se pueden guardar: usa letras sin acento, números y los signos . _ - : / ( ).',
      motivo: 'quita los caracteres que no se pueden guardar', listo: false };
  }
  const qrTego = esQrDeTego(c);
  if (qrTego) {
    return { nivel: 'rojo', tipo: t, texto: mensajeQrDeTego(qrTego),
      motivo: 'usa el código del fabricante o de otro sistema', listo: false };
  }
  const numerico = DIGITOS.test(c);
  if (c.length < LARGO_MINIMO) {
    return { nivel: 'amarillo', tipo: t,
      texto: `Un código lleva al menos ${LARGO_MINIMO} caracteres; van ${c.length}.`,
      motivo: 'completa el código', listo: false };
  }
  const tipoFinal = t || detectarTipo(c);
  const largo = LONGITUDES[tipoFinal];
  if (largo === undefined) {
    if (numerico && !t && c.length < 14) {
      // Numérico de 4 a 7 o de 9 a 11 dígitos: un EAN-13 (el de los productos que
      // se venden en México) a medio escribir, o un código interno.
      // Se dice lo primero y se ofrece marcar lo segundo (`aMedias`).
      return { nivel: 'amarillo', tipo: tipoFinal, aMedias: true,
        texto: `Un EAN-13 lleva 13 dígitos; van ${c.length}.`,
        motivo: `completa el código (van ${c.length} dígitos)`, listo: false };
    }
    return { nivel: 'gris', tipo: tipoFinal,
      texto: `${nombreDelTipo(tipoFinal)}: este tipo de código no lleva dígito que comprobar.`,
      motivo: '', listo: true };
  }
  const nombre = NOMBRES_GS1[tipoFinal];
  if (!numerico) {
    return { nivel: 'rojo', tipo: tipoFinal,
      texto: `Un ${nombre} lleva solo dígitos: revisa que no se haya colado una letra.`,
      motivo: 'deja solo dígitos', listo: false };
  }
  if (c.length < largo) {
    return { nivel: 'amarillo', tipo: tipoFinal,
      texto: `Un ${nombre} lleva ${largo} dígitos; van ${c.length}.`,
      motivo: `completa el código (van ${c.length} dígitos)`, listo: false };
  }
  if (c.length > largo) {
    return { nivel: 'rojo', tipo: tipoFinal,
      texto: `Un ${nombre} lleva ${largo} dígitos y este trae ${c.length}: sobra alguno.`,
      motivo: 'quita los dígitos que sobran', listo: false };
  }
  if (tipoFinal === 'UPCE' && upceAUpca(c) === null) {
    return { nivel: 'rojo', tipo: tipoFinal,
      texto: 'Un UPC-E empieza con 0 o con 1: revisa el primer dígito.',
      motivo: 'corrige el primer dígito', listo: false };
  }
  if (!digitoCorrecto(c, tipoFinal)) {
    return { nivel: 'rojo', tipo: tipoFinal,
      texto: 'El último dígito no corresponde a los anteriores: compara cada dígito con el impreso bajo las barras.'
        + (!t && (c.length === 8 || c.length === 12) ? ' Si el código es más largo, sigue escribiendo.' : ''),
      motivo: 'corrige el código (su último dígito no corresponde)', listo: false };
  }
  return { nivel: 'verde', tipo: tipoFinal, texto: `${nombre} válido.`, motivo: '', listo: true };
}

/** `origen` del alta en la ficha: lo que vino de un lector o de la cámara se escaneó. */
export function origenDelMedio(medio) {
  return medio === 'LECTOR' || medio === 'CAMARA' ? 'ESCANEADO' : 'TECLEADO';
}
